import React, { useState } from 'react';
import Header from '../sections/Header';
import Banner from '../sections/Banner';
import Footer from '../sections/Footer';
import SectionHeader from '../composites/SectionHeader';
import FeatureCard from '../composites/FeatureCard';
import Breadcrumbs from '../sections/Breadcrumbs';
import Button from '../primitives/Button';

const AIPolicies: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const policies = [
    {
      title: 'Integrity and Fairness',
      description: 'IE staff are obligated to take the necessary steps to prevent bias and discrimination in data, algorithms, and outcomes, ensuring equality and fairness when employing as part of their daily work or client engagements or when constructing AI systems for internal or external use.',
      category: 'Privacy',
      status: 'Active',
      lastUpdated: '2024-01-15',
      link: { href: '#', label: 'View Policy', variant: 'secondary' as const, size: 'sm' as const },
    },
    {
      title: 'Reliability & Safety',
      description: ' IE staff need to ensure that AI systems are reliable and safe and should operate according to the IE code of ethics and acceptable use. Moreover, all AI systems to be used must comply with IEs code of acceptable use of technology and needs to be approved by the AI Steering Committee.',
      category: 'Ethics',
      status: 'Active',
      lastUpdated: '2024-01-10',
      link: { href: '#', label: 'View Policy', variant: 'secondary' as const, size: 'sm' as const },
    },
    {
      title: 'Accountability & Responsibility',
      description: 'IE staff should place guardrails and controls in place to avoid harm and misuse of AI systems whether for daily work to boost productivity or as part of client delivery. AI systems should not deceive individuals or unjustifiably infringe on their freedom.',
      category: 'Security',
      status: 'Active',
      lastUpdated: '2024-01-20',
      link: { href: '#', label: 'View Policy', variant: 'secondary' as const, size: 'sm' as const },
    },
    {
      title: 'Privacy & Security',
      description: 'IE staff should ensure that companywide security practices, guidelines and controls are in place with respect to data privacy and comply with relevant Australian acts to prevent unauthorized access and potential harm when employing AI systems in the daily work and when engaging with software development activities that include the usages of the AI systems',
      category: 'Transparency',
      status: 'Draft',
      lastUpdated: '2024-01-25',
      link: { href: '#', label: 'View Draft', variant: 'secondary' as const, size: 'sm' as const },
    },
    {
      title: 'Humanity',
      description: 'IE staff should engage with AI systems using an ethical mindset to be just and ethically permissible, based on intrinsic and fundamental human rights and cultural values to foster positive impact for the wider community.',
      category: 'Governance',
      status: 'Active',
      lastUpdated: '2024-01-18',
      link: { href: '#', label: 'View Policy', variant: 'secondary' as const, size: 'sm' as const },
    },
    {
      title: 'AI Risk Assessment and Mitigation',
      description: 'Framework for identifying, assessing, and mitigating risks associated with AI implementation.',
      category: 'Risk Management',
      status: 'Active',
      lastUpdated: '2024-01-12',
      link: { href: '#', label: 'View Policy', variant: 'secondary' as const, size: 'sm' as const },
    },
  ];

  const categories = ['all', ...Array.from(new Set(policies.map(policy => policy.category)))];

  const filteredPolicies = policies.filter(policy =>
    (selectedCategory === 'all' || policy.category === selectedCategory) &&
    (policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     policy.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <Header />
      <Banner
        breadcrumbs={<Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'AI Policies' }]} />}
        title="AI Policies & Security"
        subtitle="Comprehensive policies and guidelines for responsible AI use within our organization."
        colorVariant="gradient"
        imageVariant="withImage"
        paddingVariant="default"
        showCTAs={true}
        showDefaultGraphic={true}
      />

      <div className="bg-white w-full">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <SectionHeader
                heading="AI Policies & Guidelines"
                description="Our comprehensive set of policies ensures responsible and secure AI implementation across all business functions."
                className="mb-0"
              />
            </div>
            <div className="flex flex-row gap-2 items-center w-full md:w-auto md:justify-end">
              <div className="relative w-56 md:w-48">
                <input
                  type="text"
                  placeholder="Search policies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <svg
                  className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Policies Grid */}
          {filteredPolicies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPolicies.map((policy, idx) => (
                <FeatureCard
                  key={policy.title}
                  title={policy.title}
                  description={policy.description}
                  link={policy.link}
                  className="h-full"
                  animate={true}
                  delay={idx * 0.1}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No policies found</h3>
              <p className="text-gray-600">Try adjusting your search terms or browse all policies.</p>
            </div>
          )}

          {/* Policy Request Section */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Need a New Policy?</h3>
            <p className="text-gray-600 mb-4">
              Can't find what you're looking for? Request a new AI policy or guideline that addresses your specific needs.
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {/* TODO: Implement policy request */}}
            >
              Request New Policy
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AIPolicies; 